import React, { createContext, useCallback, useState } from "react";
import {
  BillingAddressForm,
  CreateTransactionResponse,
  PaymentRetryRequest,
  ValidateTransactionRequest,
} from "../interfaces/payment.interface";
import {
  GetOverviewDetailsRequest,
  OverViewDetails,
} from "../interfaces/overview.interfaces";
import OverViewService from "../services/OverViewService";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import PaymentService from "../services/PaymentService";
import { useNotification } from "../hooks/useNotification";
import { useTranslation } from "react-i18next";
import * as uuid from "uuid";
import { isPaymentFailed, isPaymentFinished } from "../helpers";
import {
  trackOnStandardError,
  trackOnAdyenSessionCreated,
  trackOnAdyenSessionFailed,
  trackOnAdyenPaymentCompleted,
  trackOnAdyenStatusCheckSuccess,
  trackOnAdyenStatusCheckFailed,
} from "../adobeTracking/trackers";

export type PaymentContextType = {
  billingAddress: BillingAddressForm;
  setBillingAddress: React.Dispatch<BillingAddressForm>;
  OverViewDetails: OverViewDetails | undefined;
  handleGetOverViewDetails: () => void;
  transaction: CreateTransactionResponse | undefined;
  setTransaction: React.Dispatch<CreateTransactionResponse | undefined>;
  // handleCreatePayment: (billing: BillingAddressForm) => void; // DISABLED: billing address removed
  handleCreatePayment: () => void;
  handleOnAdyenSuccess: (result: any) => void;
  handleOnAdyenError: (errorMessage: string) => void;
  handleTryAgain: () => void;
  setOverViewDetails: (data: OverViewDetails | undefined) => void;
  loadingPayment: boolean;
  paying: boolean;
};

export const PaymentContext = createContext<PaymentContextType | undefined>(
  undefined,
);

interface PaymentProviderProps {
  children: React.ReactNode;
}

const PaymentProvider: React.FC<PaymentProviderProps> = ({ children }) => {
  const { login, auth, authResponse, setLoading } = useAuth();
  const { handleCreateNotification, handleDeleteAllNotifications } =
    useNotification();
  const navigate = useNavigate();
  const { t } = useTranslation("errors");
  const [OverViewDetails, setOverViewDetails] = useState<
    OverViewDetails | undefined
  >(undefined);
  const [billingAddress, setBillingAddress] = useState<BillingAddressForm>({
    companyName: "",
    companyType: "",
    address: "",
    address2: "",
    number: "",
    city: "",
    postCode: "",
    countryCode: "FR",
    email: OverViewDetails?.email ?? "",
    check: false,
  });
  const [transaction, setTransaction] = useState<
    CreateTransactionResponse | undefined
  >(undefined);
  const [loadingPayment, setLoadingPayment] = useState<boolean>(false);
  const [paying, setPaying] = useState<boolean>(false);

  const handleGetOverViewDetails = useCallback(async () => {
    try {
      if (auth && login && authResponse) {
        setLoading(true);
        const overViewService = new OverViewService(
          login?.token ?? "",
          authResponse?.nonce ?? "",
        );
        const { status, data } = await overViewService.getDetails({
          contractNumber: login?.contractNumber,
          customerNumber: login?.customerNumber,
          transactionId: authResponse.transactionId,
        } as GetOverviewDetailsRequest);

        if (status !== 200) {
          if (status === 404 && data.errorMessage === "CONTRACT_NOT_FOUND") {
            setLoading(false);
            navigate("/errors/not-authorized");
            return;
          }
          handleCreateNotification({
            id: uuid.v4(),
            status: "error",
            title:
              status !== 404
                ? status < 500
                  ? t(`requestErrors.overview.${status}.title`)
                  : t(`requestErrors.overview.500.title`)
                : undefined,
            body: (
              <React.Fragment key=".0">
                {status < 500 ? (
                  (status === 400 &&
                    data.errorMessage === "NO_OUTSTANDING_BALANCE_FOUND") ||
                  (status === 404 &&
                    data.errorMessage === "CONTRACT_NOT_FOUND") ? (
                    <>
                      <p className="u-mb">
                        {t(
                          `requestErrors.overview.${status}.${data?.errorMessage}.text1`,
                        )}
                      </p>
                      <p>
                        {t(
                          `requestErrors.overview.${status}.${data?.errorMessage}.text2`,
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="u-mb">
                        {t(`requestErrors.overview.${status}.text1`)}
                      </p>
                      <p>{t(`requestErrors.overview.${status}.text2`)}</p>
                    </>
                  )
                ) : (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.overview.500.text1`)}
                    </p>
                    <p>{t(`requestErrors.overview.500.text2`)}</p>
                  </>
                )}
              </React.Fragment>
            ),
          });
          setLoading(false);
          return;
        }

        if (data.isAlreadyFinished) {
          navigate("/confirmation/success");
          setLoading(false);
          return;
        }
        setOverViewDetails(data);
        setLoading(false);
        return;
      }
      handleDeleteAllNotifications();
      navigate("/");
    } catch (error) {
      handleCreateNotification({
        id: uuid.v4(),
        status: "error",
        title: t(`requestErrors.overview.500.title`),
        body: (
          <React.Fragment key=".0">
            <>
              <p className="u-mb">{t(`requestErrors.overview.500.text1`)}</p>
              <p>{t(`requestErrors.overview.500.text2`)}</p>
            </>
          </React.Fragment>
        ),
      });
      setLoading(false);
      trackOnStandardError("Internal Server Error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setOverViewDetails,
    auth,
    login,
    authResponse,
    setLoading,
    navigate,
    handleCreateNotification,
    t,
  ]);

  // const mapPaymentCreateTransactionData = (billing: BillingAddressForm) => {
  //   const { companyName, companyType, check, ...billingRest } = billing;
  //   return {
  //     billingAddress: billingRest,
  //     ...(companyName && companyType && { companyData: { companyName, companyType } }),
  //     transactionId: authResponse?.transactionId ?? '',
  //   };
  // };
  const mapPaymentCreateTransactionData = () => {
    return {
      transactionId: authResponse?.transactionId ?? "",
    };
  };

  const handleCreatePayment = useCallback(
    // async (billing: BillingAddressForm) => { // DISABLED: billing address parameter removed
    async () => {
      try {
        setLoadingPayment(true);
        // setBillingAddress(billing); // DISABLED: billing address no longer collected from form
        const paymentService = new PaymentService(
          login?.token ?? "",
          authResponse?.nonce ?? "",
        );
        const { status, data } = await paymentService.createTransaction(
          // mapPaymentCreateTransactionData(billing), // DISABLED: billing address removed
          mapPaymentCreateTransactionData(),
        );
        if (status !== 200) {
          if (status === 401) {
            setLoadingPayment(false);
            navigate("/errors/not-authorized");
            return;
          }
          handleCreateNotification({
            id: uuid.v4(),
            status: "error",
            title:
              status < 500
                ? t(`requestErrors.createPayment.${status}.title`)
                : t(`requestErrors.createPayment.500.title`),

            body: (
              <React.Fragment key=".0">
                {status < 500 ? (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.createPayment.${status}.text1`)}
                    </p>
                    <p>{t(`requestErrors.createPayment.${status}.text2`)}</p>
                  </>
                ) : (
                  <>
                    <p className="u-mb">
                      {t(`requestErrors.createPayment.500.text1`)}
                    </p>
                    <p>{t(`requestErrors.createPayment.500.text2`)}</p>
                  </>
                )}
              </React.Fragment>
            ),
          });
          setLoadingPayment(false);
          trackOnAdyenSessionFailed(status);
          return;
        }

        // todo
        if (data.isAlreadyFinished) {
          navigate("/confirmation/success");
          setLoadingPayment(false);
          return;
        }
        trackOnAdyenSessionCreated();
        setTransaction(data);
        setLoadingPayment(false);
        navigate("/payment");
      } catch (error) {
        handleCreateNotification({
          id: uuid.v4(),
          status: "error",
          title: t(`requestErrors.createPayment.500.title`),

          body: (
            <React.Fragment key=".0">
              <>
                <p className="u-mb">
                  {t(`requestErrors.createPayment.500.text1`)}
                </p>
                <p>{t(`requestErrors.createPayment.500.text2`)}</p>
              </>
            </React.Fragment>
          ),
        });
        setLoadingPayment(false);
        trackOnStandardError("Internal Server Error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // setBillingAddress, // DISABLED: billing address removed
      authResponse,
      setTransaction,
      setLoadingPayment,
      login,
      navigate,
      handleCreateNotification,
      t,
    ],
  );

  const handleOnAdyenSuccess = useCallback(
    async (result: any) => {
      setPaying(true);
      trackOnAdyenPaymentCompleted();
      try {
        const paymentService = new PaymentService(
          login?.token ?? "",
          authResponse?.nonce ?? "",
        );
        const { data } = await paymentService.validateTransactionStatus({
          transactionId: authResponse?.transactionId ?? "",
          reference: transaction?.reference ?? "",
        } as ValidateTransactionRequest);
        if (data && data.transactionStatus) {
          if (isPaymentFinished(data.transactionStatus)) {
            trackOnAdyenStatusCheckSuccess();
            setPaying(false);
            handleDeleteAllNotifications();
            navigate("/confirmation/success");
            return;
          }
          trackOnAdyenStatusCheckFailed(data.transactionStatus ?? "UNKNOWN");
        }
        setPaying(false);
        handleDeleteAllNotifications();
        navigate("/confirmation/error");
      } catch (error) {
        setPaying(false);
        handleDeleteAllNotifications();
        navigate("/confirmation/error");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authResponse, login, transaction],
  );

  const handleOnAdyenError = async (errorMessage: string) => {
    setPaying(true);
    try {
      const paymentService = new PaymentService(
        login?.token ?? "",
        authResponse?.nonce ?? "",
      );
      const { data } = await paymentService.validateTransactionStatus({
        transactionId: authResponse?.transactionId ?? "",
        reference: transaction?.reference ?? "",
      } as ValidateTransactionRequest);
      if (data && data.transactionStatus) {
        trackOnAdyenStatusCheckFailed(data.transactionStatus ?? "UNKNOWN");
        if (isPaymentFailed(data.transactionStatus)) {
          setPaying(false);
          handleDeleteAllNotifications();
          navigate("/confirmation/error");
          return;
        }
      }
    } catch {
      // intentionally empty — fall through to default error navigation
    } finally {
      setPaying(false);
    }
    // Default: navigate to error page if no specific routing occurred
    handleDeleteAllNotifications();
    navigate("/confirmation/error");
  };

  const handleTryAgain = async () => {
    try {
      const paymentService = new PaymentService(
        login?.token ?? "",
        authResponse?.nonce ?? "",
      );
      await paymentService.paymentRetry({
        transactionId: authResponse?.transactionId ?? "",
      } as PaymentRetryRequest);
      setTransaction(undefined);
      handleDeleteAllNotifications();
      navigate("/payment");
    } catch (error) {
      trackOnStandardError("Internal Server Error");
    }
  };
  return (
    <PaymentContext.Provider
      value={{
        billingAddress,
        setBillingAddress,
        OverViewDetails,
        handleGetOverViewDetails,
        transaction,
        setTransaction,
        handleCreatePayment,
        handleOnAdyenSuccess,
        handleOnAdyenError,
        handleTryAgain,
        setOverViewDetails,
        loadingPayment,
        paying,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};

export default PaymentProvider;
